import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import DOMUtils from '@spothero/utils/dom';
import FormatUtils from 'utils/format';
import {push} from 'router/router-actions';
import withConfig from 'config/withConfig';
import TransientMonthlyFacilityDetails from 'common/facility-details/TransientMonthlyFacilityDetails';

export class SpotListItem extends Component {
    static propTypes = {
        isInRecommendedSpotsModal: PropTypes.bool,
        isMobile: PropTypes.bool.isRequired,
        spot: PropTypes.object.isRequired,
        isAdmin: PropTypes.bool,
        isMonthly: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        cancelAllSpotRequests: PropTypes.func,
        updateSelectedSpot: PropTypes.func,
        onBookSpotClick: PropTypes.func,
        pushTo: PropTypes.func.isRequired,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        onShowDetailsClick: PropTypes.func,
    };
    state = {
        hovered: false,
    };

    constructor(props) {
        super(props);

        this._IMAGE_WIDTH = props.isMobile ? 75 : 96;
        this._IMAGE_HEIGHT = this._IMAGE_WIDTH;
    }

    componentDidMount() {
        if (!this.props.isInRecommendedSpotsModal) {
            document.addEventListener('spotHover', this._onSpotHover);
        }
    }

    componentWillUnmount() {
        if (!this.props.isInRecommendedSpotsModal) {
            document.removeEventListener('spotHover', this._onSpotHover);
        }
    }

    _onSpotHover = evt => {
        const {
            detail: {hovered, spotId},
        } = evt;

        if (spotId === this.props.spot.spotId) {
            this.setState({
                hovered,
            });
        }
    };

    _onShowDetailsClick = evt => {
        evt.preventDefault();

        const {
            spot: {spotId},
            pushTo,
            onShowDetailsClick,
        } = this.props;

        if (onShowDetailsClick) {
            onShowDetailsClick(spotId);
        }

        pushTo(`/spot-details/${spotId}`, {referrer: 'list'});
    };

    _onMouseEnter = () => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered: true,
            spotId: this.props.spot.spotId,
        });
    };

    _onMouseLeave = () => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered: false,
            spotId: this.props.spot.spotId,
        });
    };

    _onBookSpotClick = evt => {
        evt.preventDefault();
        evt.stopPropagation();

        const {
            spot,
            cancelAllSpotRequests,
            onBookSpotClick,
            updateSelectedSpot,
        } = this.props;

        if (cancelAllSpotRequests) {
            cancelAllSpotRequests();
        }

        if (onBookSpotClick) {
            onBookSpotClick(spot);
        } else {
            updateSelectedSpot({
                spot,
                referrer: 'list',
                goToSpotUrl: true,
            });
        }
    };

    _renderOfferSchema = () => {
        const {
            spot: {
                selectedRate: {
                    duration,
                    display_price: displayPriceRaw,
                    starts,
                    ends,
                    fullUrl,
                    currency_type: currencyType,
                },
                title,
            },
        } = this.props;
        const {displayPrice} = FormatUtils.price(displayPriceRaw);
        const description = `Reserved Parking at ${title} for ${duration} Hour${
            duration === 1 ? '' : 's'
        }`;

        return (
            <>
                <meta itemProp="name" content="Parking" />
                <meta itemProp="url" content={fullUrl} />
                <meta itemProp="description" content={description} />
                <meta itemProp="priceCurrency" content={currencyType} />
                <meta itemProp="price" content={displayPrice} />
                <meta
                    itemProp="availability"
                    content="http://schema.org/InStock"
                />
                <meta itemProp="validFrom" content={starts} />
                <meta itemProp="validThrough" content={ends} />
                <span
                    itemProp="offeredBy"
                    itemScope
                    itemType="http://schema.org/Organization"
                >
                    <meta itemProp="name" content="SpotHero" />
                    <meta itemProp="url" content="https://spothero.com" />
                </span>
            </>
        );
    };

    render() {
        const {
            isInRecommendedSpotsModal,
            isMobile,
            spot,
            isMonthly,
            isAdmin,
            isRebookingReservation,
            isLowInventoryEnabled,
            isSoldOutEnabled,
        } = this.props;
        const {
            selectedRate,
            activeMarker,
            spotId,
            available_transient_inventory: transientInventory,
            available_monthly_inventory: monthlyInventory,
        } = spot;
        const {hovered} = this.state;
        const {fullUrl, unavailable} = selectedRate;
        const resultType = isMonthly ? 'monthly' : 'hourly';
        const inventory =
            isMonthly && monthlyInventory
                ? monthlyInventory.default_rate
                : transientInventory;
        const isSoldOut =
            isSoldOutEnabled && !isMonthly && inventory === 0 && !isAdmin;
        const containerClasses = classNames(
            'SpotListItem',
            `${resultType}-result`,
            `result${spotId}`,
            {'SpotListItem-active': activeMarker},
            {'SpotListItem-hovered': hovered},
            {'SpotListItem-sold-out': isSoldOut},
            {unavailable}
        );
        const bookItClasses = classNames('SpotListItem-book-it', {
            'admin-border-button': isAdmin && isRebookingReservation,
        });
        const onBookClick = isMonthly
            ? isInRecommendedSpotsModal
                ? this._onBookSpotClick
                : null
            : this._onBookSpotClick;
        const showDetailsBtn =
            (!isSoldOutEnabled &&
                !isLowInventoryEnabled &&
                !isMonthly &&
                !isMobile) ||
            (isMonthly && isMobile);
        const showBookBtn = !isMobile || (isMobile && !isMonthly);

        return (
            <div
                className={containerClasses}
                data-spot={spotId}
                itemProp="offers"
                itemScope
                itemType="http://schema.org/Offer"
                onMouseEnter={
                    !isInRecommendedSpotsModal ? this._onMouseEnter : null
                }
                onMouseLeave={
                    !isInRecommendedSpotsModal ? this._onMouseLeave : null
                }
                onClick={
                    !isInRecommendedSpotsModal && !isSoldOut
                        ? this._onShowDetailsClick
                        : null
                }
            >
                {this._renderOfferSchema()}
                <div
                    itemProp="availableAtOrFrom"
                    itemScope
                    itemType="http://schema.org/ParkingFacility"
                >
                    <TransientMonthlyFacilityDetails
                        spot={spot}
                        isMonthly={isMonthly}
                        imageWidth={this._IMAGE_WIDTH}
                        imageHeight={this._IMAGE_HEIGHT}
                        isAdmin={isAdmin}
                        isLowInventoryEnabled={isLowInventoryEnabled}
                    />
                </div>
                <div className="controls">
                    {showDetailsBtn && (
                        <TextButton className="show-details">
                            {isMobile ? 'View ' : ''}Details
                        </TextButton>
                    )}
                    {isAdmin && unavailable ? (
                        <Button color="primary" itemProp="url" disabled>
                            Unavailable <sup className="text-light">*</sup>
                        </Button>
                    ) : (
                        showBookBtn &&
                        !isSoldOut && (
                            <TextButton
                                className={bookItClasses}
                                href={fullUrl}
                                color="primary"
                                itemProp="url"
                                onClick={onBookClick}
                            >
                                {isRebookingReservation
                                    ? 'Rebook It'
                                    : 'Book Now'}
                            </TextButton>
                        )
                    )}
                </div>
                {isSoldOut && (
                    <div className="SpotListItemRow-sold-out">
                        <p>Sold Out</p>
                    </div>
                )}
            </div>
        );
    }
}

const mapDispatchToProps = {
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        null,
        mapDispatchToProps
    )
);

export default enhance(SpotListItem);
